<template>
  <div id="tags-menu"
       :class="{open: isTagsMenuOpen && !!currentTagsType, static: isTagsMenuPinned, overflow: isTagsMenuOverflow && isTagsMenuOverflow.enabled && isTagsMenuOpen}"
       v-click-outside="closeTagMenu">

    <div id="tags-menu-header" :class="{ 'tags-menu-pinned' : isTagsMenuPinned }">
      <i
        v-if="!isTagsMenuPinned"
        role="button"
        class="fas fa-times"
        @click.prevent="closeTagMenu"
      ></i>
      <i
        id="tags-menu-pin"
        role="button"
        class="fas fa-thumbtack"
        @click="toggleMenuPin"
      >
      </i>
    </div>
    <div id="parent-category" class="shortcuts shortcut" v-if="parentTag">
      <div 
        :id="`shortcode-${parentTag.key}`"
        :class="{ [parentTag.category]: parentTag.category }"
        class="cursor-pointer shortcut"
        role="button"
        @click.prevent="onTagParentClick"
      >
        <span class="fas fa-plus" v-if="parentTag.allow_add || isAddOrgTag(parentTag)" v-on:click="(e) => toggleAddTagModal(e, true, true, parentTag)" :style="{color: parentTag.color}"></span>
        <a
          :style="{ background: parentTag.color }"
          @click.prevent="loadTags"
        >
          <i v-if="showPinned" class="fas fa-long-arrow-left"></i>
          <i v-else class="fa fa-ban" aria-hidden="true"></i>
        </a>
        {{ parentTag.name }}
      </div>
    </div>
    <div id="side-search-editor">
      <div id="main-categories-wrapper">
        <div>
          <div class="form-group search-input">
            <label>{{ $t("search") }}<span>({{tagSearchShortcut}})</span></label>
            <div class="form-control">
              <input type="text" id="main-search" class="input-ui"
                     ref="tagSearchInput"
                     v-model="searchQueryText"
                     v-on:keyup="delayFunction(queryTags, 500)" />
              <span class="fa fa-search"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="side-editor" ref="sideEditor">
      <div id="sub-categories-wrapper" ref="sideEditorWrapper"> 
        <div id="side-tags">
          <div id="pinned-sub-categories" class="shortcuts right" v-if="pinnedTags">
            <div v-for="(tag) in pinnedTags" :key="'pin' + tag.key"
                 :class="{shortcut: true, [tag.category]: tag.category, pinned: tag.pinned}"
                 :id="'pin-shortcode-'+tag.key"
                 v-on:click="(e) => onTagClick(e, tag)">
              <span :class="['fas', loadingTags.includes(tag.key) ? 'fa-spinner-third fa-spin' : 'fa-thumbtack']" v-on:click="(e) => toggleTagPin(e, tag)" :style="{color: tag.color}"></span>
              <div :style="{background: tag.color}">{{ tag.key }}</div>{{ tag.name }}
            </div>
          </div>
          <app-load-content :loading="loadingFirstPage">
            <ul
              v-if="displayTagsLen"
              id="sub-categories"
              class="shortcuts right"
            >
              <li
                v-for="(tag, key) in filteredDisplayTags"
                v-tooltip="tag.name.length > 40 ? tag.name : null"
                :id="'shortcode-'+key"
                :key="key"
                :style="getStyleItem(tag, key)"
                class="shortcut cursor-pointer"
                :class="{
                  [tag.category]: tag.category,
                  pinned: tag.pinned,
                  disabled: analysisArticleLoading
                }"
                @click="(e) => onTagClick(e, tag)"
                :aria-disabled="analysisArticleLoading"
              >
                <span
                  v-if="!tag.approved && tag.created_by === user.id"
                  :style="{color: tag.color}"
                  class="fas fa-pencil-alt"
                  role="button"
                  @click="(e) => onEditTagClick(e, tag)"
                ></span>
                <span
                  v-if="!tag.approved && tag.created_by === user.id"
                  class="fa fa-times"
                  role="button"
                  @click.prevent.stop="onDeleteTagClick(tag)"
                ></span>
                <span
                  v-if="tag.parent && tag.approved && (showPinned || !parentTag)"
                  :class="['fas', loadingTags.includes(key) ? 'fa-spinner-third fa-spin' : 'fa-thumbtack']"
                  :style="{color: tag.color}"
                  role="button"
                  @click="(e) => toggleTagPin(e, tag)"
                ></span>
                <div :style="{background: tag.color}">{{ key }}</div>
                {{ tag.name.length > 40 ?`${tag.name.slice(0,35)}...` : tag.name }} 
              </li>
            </ul>
            <div 
              v-else
              class="text-sm text-white pt10 opaque"
            >
              {{ noTagsMessage }}
            </div>
          </app-load-content>
        </div>
      </div>
    </div>
    <app-modal v-if="showTagModal" @closeModal="closeTagModal" class="click-outside-categories-ignore">
      <div slot="header">
        <h3>{{ $t('tags.add_tag_header') }}</h3>
      </div>
      <form-renderer
        slot="body"
        id="edit-tag-form"
        ref="add-edit-form"
        :fields="['name']"
        :customValidators="customValidators"
        :model="editTag"
        :errors="tagsErrorsMessages"
        submitButtonLabel="form.button.save"
        @onSave="addEditTag"
        @onCancel="closeTagModal"
        :loading="organizationsIsBusy"
        :showSubmit="!associationCategoriesOptions.length"
      />
    
      <div v-if="associationCategoriesOptions.length" class="form-wrapper">
        <div class="form">
          <h4 class="text-center">{{ $t('tags.add_associated_tags') }}</h4>
          <!-- Scrollable section -->
          <div class="scroll-container">
            <div class="row" v-for="associated_tag in associationTags.child_tags" :key="associated_tag.temp_id">
              <div class="flex gap-4">
                <div class="form-group flex-1">
                  <label for="">{{$t("relations.associations.category")}}</label>
                  <input-select
                    name="parent_category_id"
                    ref="parent_category_id"
                    :searchable="true"
                    :selectedValue="associated_tag.category_id"
                    :options="associationCategoriesOptions"
                    :onChange="(value) => onChangeChildCategory(associated_tag.temp_id, value)"
                  />
                </div>
              
                <div class="form-group flex-1">
                  <label for="">{{$t("relations.associations.tag")}}</label>
                  <input-select
                    name="associated_tag_id"
                    ref="associated_tag_id"
                    :selectedValue="associated_tag.id"
                    :searchable="true"
                    :preserveSearch="true"
                    :internalSearch="false"
                    :options="categoryTagsOptions"
                    :onChange="(value) => onChangeChildTag(associated_tag.temp_id, value)"
                    :onSearchChange="(searchText) => onSearchChange(searchText, associated_tag.category_id, associated_tag.temp_id)"
                    :loading="projectCategorySetupIsLoading && loadingElement == associated_tag.temp_id"
                  />
                </div>
              </div>
            </div>
          </div>
    
          <div class="btn-container">
            <button class="btn btn-default btn-sm" @click="addChildTag">
              <i class="fas fa-plus"></i> {{$t("relations.actions.add")}}
            </button>
            <button class="btn btn-carma btn-sm" @click="handleSaveButtonClick">
              <i class="fas fa-check"></i> {{$t("relations.actions.save")}}
            </button>
          </div>
        </div>
      </div>
    </app-modal>

    <app-confirmation-modal v-if="showConfirmDeleteModal"
                            class="click-outside-categories-ignore"
                            :headerText="$t('tags.delete_tag_header')"
                            :message="$t('tags.delete_tag_body', [editTag.name])"
                            :showDeleteButton="true"
                            @confirmAction="onConfirmDeleteTag"
                            @cancelAction="closeConfirmDeleteModal" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash';

import { TAG_TYPE, TAG_CATEGORY, CUSTOM_VALIDATIONS } from '../../config/const';
import SHORTCUTS from '../../config/shortcut-keys';
import editor from '../../helpers/editor';

import Modal from '../modal';
import FormRenderer from '../form-renderer';
import ConfirmationModal from '../confirmation-modal';
import LoadContent from '../load-content';

import customValidation  from "../../mixins/forms/customValidation";
import { clearCase } from "@/helpers/case";
import FormHelper from '@/helpers/form';
import InputSelect from '@/components/form-components/input-select';


export default {
  props: {
    isTagsMenuPinned: Boolean,
    isTagsMenuOpen: Boolean,
    isTagsMenuOverflow: Object,
    isTagsBusy: {
      type: Boolean,
      default: false
    },
    currentTagsType: String,
    displayTags: Object,
    categoryKey: [Number, String],
    searchQuery: String,
    briefMappings: Object,
    menuType: String,
    activeOrganizationObject: Object,
    selectedTagKey: {
      type: String,
      required: false
    }
  },
  mixins: [customValidation],
  components: {
    'app-load-content': LoadContent,
    'app-confirmation-modal': ConfirmationModal,
    'app-modal': Modal,
    FormRenderer,
    InputSelect
  },
  created() {
    this.mapValidationFunctions()
  },
  mounted() {
    let sideEditor = this.$refs.sideEditor; 
    if (sideEditor) {
      sideEditor.addEventListener('scroll', this.scroll);
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll);
  },
  computed: {
    ...mapState('tag', ['tag']),
    ...mapGetters('tag', ['tagsErrors', 'tagIsBusy']),
    ...mapState({
      user: state => state.user.user.data,
      tags: state => state.tags.tags.categoryData,
      tagsPagination: state => state.tags.tags.categoryData.pagination,
      loading: state => state.tags.tags.tagsIsBusy,
      projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general,
      briefMappingsIsLoading: state => state?.briefMappings?.briefMappings?.isBusy,
      loadingTags: state => state?.tag?.tag?.loadingTags,
      organizations: state => state?.organizations?.organizations?.data?.data,
      analysisArticleLoading: state => state.analysisArticle.analysisArticle.isBusy,
      projectCategories: state => state.projectSetup.projectCategorySetup?.data || [],
      projectCategorySetupIsLoading: state => state.projectSetup?.projectCategorySetup?.isBusy,
    }),
    associationCategoriesOptions() {
       const options = this.projectCategories.filter(c => c.allow_association === true).map(c => ({ value: c.id, label: c.name }));
       if(options.length > 0 && !this.associationTags.child_tags.length) {
          this.addChildTag();
       }
       return options;
    },
    loadingFirstPage() {
      return this.isTagsBusy && this.pagination.page === 1;
    },
    projectId() {
      return this.$route.params.project_id;
    },
    pinnedTags() {
      if(this.briefMappings?.data) {
        return _.filter(this.briefMappings.data.data, function(m) { return m.pinned; })
      }
      return [];
    },
    filteredDisplayTags() {
      let filteredObject = {};
      let _displayTags = _.cloneDeep(this.displayTags);
      if (Object.keys(_displayTags || {})?.length > 0) {
        Object.keys(_displayTags).map(tag => {
          if (!this.pinnedTags.find(pinTag => pinTag.key === tag)) {
            filteredObject[tag] = _displayTags[tag];
            // make sure its false when relocated to the not pinned tags list
            filteredObject[tag].pinned = false; 
          }
        });
      }
      return filteredObject;
    },
    parentTag() {
      if(this.categoryKey && this.briefMappings?.data) {
        const catKey = this.categoryKey;
        return _.find(this.briefMappings.data.data, function(m) { return m.key === catKey; })
      }
      return undefined;
    },
    tagSearchShortcut () {
      return editor.getShortcutAsString(SHORTCUTS.shortcuts.search, '+');
    },
    displayTagsLen() { return Object.keys(this.filteredDisplayTags).length > 0 },
    noTagsMessage() {
      if(this.currentTagsType === 'categories') return this.$t('tags_menu.category_tag_empty');
      if(this.currentTagsType === 'organizations' && this.isUseOrgWithCategory && !this.searchQuery) return this.$t('tags_menu.setup_organization')
      else if(this.currentTagsType === 'organizations') return this.$t('tags_menu.organization_tag_empty')
      return 'Tags Found'
    },
    tagsErrorsMessages() {
      const errors = [];

      if (this.tagsErrors && this.tagsErrors.message)
        errors.push(this.tagsErrors.message);

      return errors;
    },
    isUseOrgWithCategory() { return this.projectSetupGeneral?.organizations_with_category },
    showPinned(){
      return !this.isUseOrgWithCategory || (this.isUseOrgWithCategory && this.parentTag && this.parentTag?.id !== this.projectSetupGeneral?.organizations_category_id );
    }
  },
  watch: {
    'tag.data' (value) {
      this.reloadTags(value?.data?.parent);
    },
    'parentTag' () {
      this.pagination.page = 1;
    },
    'briefMappings.data' (data) {
      if (data?.message !== 'cancelled') this.$store.commit('tag/LOADING_SUCCESS_TAG', []);
    },
    'showTagModal' (isOpen) {
      if (isOpen && this.projectCategories.length) {
        // Initialize options for existing child tags that already have category_id
        this.$nextTick(() => {
          this.initializeCategoryOptions();
        });
      }
    },
    'projectCategories' () {
      if (this.showTagModal && this.projectCategories.length) {
        // Initialize options when projectCategories become available
        this.$nextTick(() => {
          this.initializeCategoryOptions();
        });
      }
    }
  },

  methods: {
    ...mapActions({
      'updateTag': 'tag/updateTag',
      'toggleTagPinAction': 'tag/toggleTagPin',
      'getCategoryTags': 'tags/getCategoryTags',
      'addTag': 'tag/addTag',
      'deleteTag': 'tag/deleteTag',
      'clearTagError': 'tag/clearError',
      'createOrganization': 'organizations/createOrganization',
      'getProjectCategoriesSetup': 'projectSetup/getProjectCategoriesSetup'
    }),

    addChildTag: function() {
      const tag = { id: null, name: null, category_id: null, temp_id: this.generateTempId() };
      this.associationTags.child_tags.push(tag);
    },

    onChangeChildCategory: function(temp_id, value) {
      this.categoryTagsOptions = this.projectCategories.find(c => c.id == value)?.tags?.map(t => ({ value: t.id, label: t.name })) || [];
      this.associationTags = {
        ...this.associationTags,
        child_tags: this.associationTags.child_tags.map(t => t.temp_id == temp_id ? { id: null, name: null, temp_id: t.temp_id, category_id: value } : t)
      }
    },

    onChangeChildTag: function(temp_id, value) {
      this.associationTags = {
        ...this.associationTags,
        child_tags: this.associationTags.child_tags.map(t => t.temp_id == temp_id ? { ...t, id: value } : t)
      }
    },

    onSearchChange(searchText, categoryId, loadingElement) {
      FormHelper.delayFunction(this.onSearch(searchText, categoryId), 500, this, loadingElement);
    },

    generateTempId: function() {
      return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    },
    onSearch (searchText, parentId) {
      this.globalTimeout = null;
      if(!parentId || !this.isUseOrgWithCategory) return;
      const filters = {tag_name: searchText?.trim(), cat_id: parentId};
      this.fetchProjectCategories(filters)
    },

    fetchProjectCategories(filters) {
      const pagination = { page: 1, perPage: 20, order: 'name', sort: 'asc' };
      this.getProjectCategoriesSetup({ projectUid: this.projectId, pagination, filters}).then(data => {
        let hasTags = data.find(c => c.id == filters.cat_id)?.tags?.length > 0; 
        this.categoryTagsOptions = hasTags ? data.find(c => c.id == filters.cat_id)?.tags?.map(t => ({ value: t.id, label: t.name })) : [{ value: null, label: this.$t('tag.list.not_found') }];
      })
    },

    initializeCategoryOptions() {
      // Initialize options for child tags that already have a category_id
      this.associationTags.child_tags.forEach(childTag => {
        if (childTag.category_id && !this.categoryTagsOptions.find(c => c.value == childTag.category_id)) {
          this.categoryTagsOptions.push({ value: childTag.category_id, label: this.projectCategories.find(c => c.id == childTag.category_id)?.name || '' });
        }
      });
    },

    hexToRgba (value, alpha = 1) {
      return editor.hexToRgba(value, alpha);
    },
    getStyleItem(tag, key) {
      if (this.selectedTagKey !== key && !tag.color) return;
      else if (this.selectedTagKey === key && !tag.color) return ` background: linear-gradient(to right, #2985a3, #fff); color: #000;`;
      return this.selectedTagKey === key
        ? `background: linear-gradient(to right, ${this.hexToRgba(tag.color, 1)}, #fff; color: #000`
        : 'background: #fff';
    },
    toggleMenuPin () {
      this.$emit('toggleMenuPin', !this.isTagsMenuPinned);
    },
    loadTags() {
      if(!this.showPinned) return false;
      const { projectId, pagination, searchQuery } = this;
      let filters = {}
      if(this.isUseOrgWithCategory) {
        filters = { organizations_category_id: this.projectSetupGeneral?.organizations_category_id };
        // show organization parent category with allowed back arrow, to load organization categories back
        if(this.parentTag && this.currentTagsType === TAG_TYPE.ORGANIZATION) {
          filters.active_organization_id = this.parentTag?.id;
          this.$emit('updateTagsMenuData', this.isTagsMenuOpen, TAG_TYPE.CATEGORY, this.isTagsMenuOverflow, this.searchQueryText, this.parentTag?.parent, undefined, undefined, undefined, this.isUseOrgWithCategory);
          return;
        }
      }
      this.getCategoryTags({ projectId, pagination, searchQuery, filters });
    },
    closeTagMenu (e) {
      if(this.isTagsMenuOpen &&
        !e.target.classList.contains('click-outside-categories-ignore') &&
        e.target.closest('.click-outside-categories-ignore') === null)
      {
        this.searchQueryText = '';
        this.$emit('updateTagsMenuData', false, '');
      }
    },
    toggleTagPin (e, tag) {
      e.preventDefault();
      e.stopPropagation();

      // prevent click if its in loading state
      if((this.tagIsBusy || this.briefMappingsIsLoading) && this.loadingTags.includes(tag?.key)) return;

      this.toggleTagPinAction({
        projectId: this.projectId,
        tag: tag
      });
    },
    onTagClick (e, tag) {
      e.preventDefault();
      e.stopPropagation();

      //if tag clicked is a category, update menu to show tags assigned to the category
      //else add the tag to the mentions
      if (tag.category !== TAG_CATEGORY.ORGANIZATION && tag.tags) {
        this.$emit('updateTagsMenuData', true, TAG_TYPE.CATEGORY, this.isTagsMenuOverflow, this.searchQuery, tag.key);
      }
      else {
        this.$emit('addMentionTag', tag);
        this.searchQueryText = '';

        //If tag added is an organisation, show category tags menu
        if(tag.category === TAG_CATEGORY.ORGANIZATION) {
          this.$emit('updateTagsMenuData', !this.isUseOrgWithCategory, TAG_TYPE.CATEGORY, this.isTagsMenuOverflow, this.searchQuery, undefined, undefined, undefined, this.isUseOrgWithCategory && tag);
        }
      }
    },
    onTagParentClick (e) {
      if(!this.showPinned) return false;

      e.preventDefault();
      e.stopPropagation();

      this.$emit('updateTagsMenuData', true, TAG_TYPE.CATEGORY, this.isTagsMenuOverflow, this.searchQueryText, undefined, true);
    },
    delayFunction (callback, delayMs) {
      if(this.globalTimeout != null)
        clearTimeout(this.globalTimeout);

      this.globalTimeout = setTimeout(callback, delayMs);
    },
    queryTags () {
      this.globalTimeout = null;

      if(this.categoryKey) {
        this.categorySearchQuery = this.searchQuery;
      }
      this.$emit('updateTagsMenuData', true, this.currentTagsType, this.isTagsMenuOverflow, this.searchQueryText, this.categoryKey);
    },
    isUsedName(tag) {
      return this.organizations.find((item) => {
        return clearCase(item?.name) === clearCase(tag?.name);
      });
    },
    nameIsValid(tag) {
      return clearCase(tag?.name) && !this.isUsedName(tag);
    },
    isAddOrgTag(tag){
      return this.isUseOrgWithCategory && this.currentTagsType === TAG_TYPE.ORGANIZATION && tag?.parent;
    },
    toggleAddTagModal(e, open, isAdd, tag) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.showTagModal = open;
      if(isAdd) {
        // if its an organization tag adding, handle tag data for adding,
        // not to set/change a parent attr or remove all tag data, because tag.parent is needed later
        if(this.isAddOrgTag(tag)) {
          let _tag = _.cloneDeep(tag);
          _tag.name = '';
          this.editTag = _tag;
          return _tag;
        }
        else{
          const parent = tag.key;
          tag = {};
          tag.parent = parent;
        }
      }
      this.editTag = tag;

      this.clearTagError();
    },
    onEditTagClick (e, tag) {
      this.toggleAddTagModal(e, true, false, tag);
    },
    addEditTag(tag) {
      if (!tag) return;

      this.clearTagError();

      const data = {
        projectId: this.projectId,
        tag: tag
      };

      // if this tag is organization category tag, allow adding tag to organizations
      if(this.isAddOrgTag(tag)) {
        if(!this.nameIsValid(tag)) return;

        // prepare needed data to create new organization tag
        data.parent_tag_id = tag?.id;
        data.name = tag?.name;
        data.child_tags = tag.child_tags;

        this.organizationsIsBusy = true;

        this.createOrganization({
          projectId: data?.projectId,
          organization: data,
        })
        .then(() => {
          this.closeTagModal();
        })
        .finally(() => {
          this.organizationsIsBusy = false;
        });
        return;
      }
      
      if (tag.key) {
        this.updateTag(data)
          .then(response => {
            this.closeTagModal();
          });

        this.$emit('updateTagInMentions', tag);
      } else {
        this.addTag(data)
          .then(response => {
            this.closeTagModal();
          });
      }

    },
    handleSaveButtonClick() {
      // Get the form data from the form-renderer component
      const formData = this.$refs['add-edit-form']?.formModel || this.editTag;
      formData.child_tags = this.associationTags.child_tags;
      this.addEditTag(formData);
    },
    onConfirmDeleteTag(){
      const tagToDelete = _.cloneDeep(this.editTag);
      this.deleteTag({
        projectId: this.projectId,
        tagKey: this.editTag.key
      });
      this.$emit('removeTagFromMentions', tagToDelete);

      this.closeConfirmDeleteModal();
    },
    reloadTags(categoryKey) {
      this.$emit('updateTagsMenuData', this.isTagsMenuOpen, this.currentTagsType, this.isTagsMenuOverflow, this.searchQueryText, this.categoryKey, true, undefined, undefined, this.isUseOrgWithCategory);
      this.$emit('refreshBriefMappings', true, categoryKey);
    },
    closeTagModal () {
      this.showTagModal = false;
      this.associationTags.child_tags = [];
      this.categoryTagsOptions = [];
    },
    onDeleteTagClick (tag) {
      this.editTag = tag;
      this.showConfirmDeleteModal = true;
    },
    closeConfirmDeleteModal () {
      this.editTag = undefined;
      this.showConfirmDeleteModal = false;
    },
    scroll() {
      if (this.tagsPagination && this.tagsPagination.currentPage < this.tagsPagination.totalPages) {
        const sideEditorHeight = this.$refs.sideEditor.clientHeight;
        const { top, height } = this.$refs.sideEditorWrapper.getBoundingClientRect();
        const margin = 122;

        if (top * -1 + sideEditorHeight >= height - margin && this.isTagsBusy == false) {
          this.pagination.page += 1;
          const {projectId, pagination, categoryKey, searchQuery} = this;
          this.getCategoryTags({projectId, pagination, categoryKey, searchQuery})
        }
      }
    }
  },
  data () {
    return {
      TAG_TYPE: TAG_TYPE,
      customFormValidation: CUSTOM_VALIDATIONS.add_edit_tag_form,
      searchQueryText: '',
      categorySearchQuery: '',
      globalTimeout: null,
      showTagModal: false,
      editTag: null,
      showConfirmDeleteModal: false,
      validationMessages: [],
      organizationsIsBusy: false,
      associationTags: {child_tags:[]},
      categoryTagsOptions: [],
      pagination: {
        page: 1,
        perPage: 100,
        order: "id",
        sort: "asc"
      }
    }
  }
}
</script>

<style lang="scss">
@import "../../styles/components/coding-ui/tags-menu";
.scroll-container {
  height: 200px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
}
</style>